/**
 * Settings, cut back to what is actually wired.
 *
 * The design shipped seven tabs and one of them worked. The rest were the
 * shape of a settings screen without anything behind it: a workspace name and
 * a default currency that saved nowhere, five notification toggles that held
 * state until the page reloaded, a "Default landing page" that changed
 * nothing, a link to an Admin area that does not exist, an Export that raised
 * "check your downloads" without writing a file, and a Delete workspace button
 * whose own confirmation dialog said, in the copy, that it was a mock.
 *
 * The profile tab was worse than useless: it filled the name, email and job
 * title with a person who does not work here, so the one screen that answers
 * "who am I signed in as" answered wrong.
 *
 * None of that is a port bug — it is a demo doing what a demo is for. It stops
 * being harmless the moment it is in front of somebody who believes it. A
 * toggle that does nothing teaches people the setting is on. A delete button
 * that does nothing is inherited, one day, by a version that does.
 *
 * So each tab came back only with a server route behind it: the studio's
 * name, week, currency and financial year; a person's notifications, landing
 * page, date format and auto-assign (stored on them, `PATCH /me`); what their
 * role may do; and exports that write a real file. Delete workspace did not
 * come back.
 */

import { Check } from 'lucide-react'
import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Avatar } from '../components/Avatar'
import { Button } from '../components/Button'
import { PageHeader } from '../components/PageHeader'
import { SegmentedControl } from '../components/FilterBar'
import { ACCENT_OPTIONS, useAppearance } from '../context/AppearanceContext'
import { useAuth, type AuthUser } from '../context/AuthContext'
import { StudioTab } from './settings/StudioTab'
import { DataTab, NotificationsTab, PermissionsTab, PreferencesTab } from './settings/PersonalTabs'
import { PasswordRow } from './settings/PasswordRow'
import { useToast } from '../context/ToastContext'
import { useAppData } from '../context/AppDataContext'
import { apiErrorMessage } from '../lib/api'
import { squarePhoto } from '../lib/photo'
import { Field } from '../ui/Field'


const selectClass =
  'h-9 rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text-active outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100'

const tabs = [
  { label: 'Profile', value: 'account' },
  { label: 'Workspace', value: 'studio' },
  { label: 'Notifications', value: 'notifications' },
  { label: 'Appearance', value: 'appearance' },
  { label: 'Preferences', value: 'preferences' },
  { label: 'Permissions', value: 'permissions' },
  { label: 'Data', value: 'data' },
]

const ROLE_LABEL: Record<string, string> = {
  PARTNER: 'Admin',
  MANAGER: 'Manager',
  TEAM: 'Team Member',
  VIEWER: 'Viewer',
}

function SettingsRow({
  title,
  description,
  children,
}: {
  title: string
  description?: string | undefined
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <div>
        <p className="text-sm font-medium text-shell-text-active">{title}</p>
        {description !== undefined && <p className="mt-0.5 text-xs text-shell-text">{description}</p>}
      </div>
      {children}
    </div>
  )
}

/**
 * Your own entry on the roster: the part of it that is yours to change.
 *
 * Name, title and photo are yours. Role and capacity are a partner's call and
 * are shown, not offered, here; a partner changes them on Admin, Team.
 */
function AccountTab({ currentUser, logout }: { currentUser: AuthUser; logout: () => void }) {
  const { showToast } = useToast()
  const { refresh } = useAuth()
  const { updateMe, photoFor, setPhoto, clearPhoto } = useAppData()
  const personId = currentUser.linkedPersonaId

  const [name, setName] = useState(currentUser.name)
  const [title, setTitle] = useState(currentUser.title ?? '')
  const [saving, setSaving] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const photo = personId === null ? undefined : photoFor(personId)

  const changed = name.trim() !== currentUser.name || title.trim() !== (currentUser.title ?? '')
  const problem = name.trim() === '' ? 'Your name cannot be empty.' : null

  async function save() {
    setSaving(true)
    try {
      await updateMe({ name: name.trim(), title: title.trim() })
      // The header and the sidebar read your name from the session.
      await refresh()
      showToast('Saved.')
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setSaving(false)
    }
  }

  async function upload(file: File) {
    if (personId === null) return
    if (!file.type.startsWith('image/')) {
      showToast('That is not a picture. Choose a JPEG, PNG or WebP.', 'error')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      showToast('That picture is over 15 MB. Choose a smaller one.', 'error')
      return
    }
    setPhotoBusy(true)
    try {
      await setPhoto(personId, await squarePhoto(file))
      showToast('Photo updated.')
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setPhotoBusy(false)
    }
  }

  async function removePhoto() {
    if (personId === null) return
    setPhotoBusy(true)
    try {
      await clearPhoto(personId)
      showToast('Photo removed.')
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setPhotoBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-4">
        <Avatar name={currentUser.name} color={currentUser.color} size="lg" photo={photo} />
        <div className="min-w-0">
          <p className="font-display text-lg font-semibold text-shell-text-active">{currentUser.name}</p>
          <p className="text-sm text-shell-text">
            {currentUser.title ?? ROLE_LABEL[currentUser.role] ?? currentUser.role}
          </p>
        </div>
        {personId !== null && (
          <div className="flex items-center gap-2 sm:ml-auto">
            <Button variant="secondary" size="sm" disabled={photoBusy} onClick={() => fileInput.current?.click()}>
              {photoBusy ? 'Working…' : photo === undefined ? 'Upload photo' : 'Change photo'}
            </Button>
            {photo !== undefined && (
              <Button variant="ghost" size="sm" disabled={photoBusy} onClick={() => void removePhoto()}>
                Remove
              </Button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                // Cleared at once, so choosing the same file again still fires.
                e.target.value = ''
                if (file !== undefined) void upload(file)
              }}
            />
          </div>
        )}
      </div>

      <div className="divide-y divide-shell-border border-t border-shell-border">
        {personId === null ? (
          <SettingsRow
            title="Your name and photo"
            description="Your sign-in is not linked to anybody on the roster yet. A partner can link it from Admin, Team."
          >
            <span className="shrink-0 text-sm text-shell-text">Not linked</span>
          </SettingsRow>
        ) : (
          <div className="flex flex-col gap-3 py-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="Name"
                value={name}
                maxLength={80}
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
              />
              <Field
                label="Title"
                value={title}
                maxLength={60}
                placeholder="Video editor, Strategist…"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              {problem !== null && changed && <p className="text-xs text-danger-500">{problem}</p>}
              <Button size="sm" disabled={!changed || problem !== null || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
        <SettingsRow title="Your role" description="A partner changes roles and capacity from Admin, Team.">
          <span className="shrink-0 rounded-full bg-shell-border/40 px-2.5 py-1 text-xs font-medium text-shell-text-active">
            {ROLE_LABEL[currentUser.role] ?? currentUser.role}
            {currentUser.capacity !== null && ` · ${currentUser.capacity} points a week`}
          </span>
        </SettingsRow>
        <PasswordRow />
      </div>

      <Button variant="secondary" className="self-start" onClick={() => void logout()}>
        Sign out
      </Button>
    </div>
  )
}

export default function Settings() {
  const { showToast } = useToast()
  const { currentUser, logout } = useAuth()
  const [tab, setTab] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get('tab')
    return tabs.some((t) => t.value === asked) ? (asked as string) : 'account'
  })
  const { theme, density, accentColor, setTheme, setDensity, setAccentColor } = useAppearance()

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Settings"
        description="Who you are, how the studio works, and how the app behaves for you."
      />

      <div className="overflow-x-auto scrollbar-thin">
        <SegmentedControl value={tab} onChange={setTab} options={tabs} />
      </div>

      <div className="max-w-2xl rounded-card border border-shell-border bg-shell p-6 shadow-card">
        {tab === 'studio' && <StudioTab />}
        {tab === 'notifications' && <NotificationsTab />}
        {tab === 'preferences' && <PreferencesTab />}
        {tab === 'permissions' && <PermissionsTab />}
        {tab === 'data' && <DataTab />}

        {tab === 'appearance' && (
          <div className="divide-y divide-shell-border">
            <SettingsRow
              title="Theme"
              description="Dark applies to the sidebar, header, and mobile nav; content cards stay light for readability."
            >
              <SegmentedControl
                value={theme}
                onChange={(v) => {
                  setTheme(v as 'light' | 'dark' | 'system')
                  showToast(`Theme set to ${v}.`, 'info')
                }}
                options={[
                  { label: 'Light', value: 'light' },
                  { label: 'Dark', value: 'dark' },
                  { label: 'System', value: 'system' },
                ]}
              />
            </SettingsRow>
            <SettingsRow
              title="Density"
              description="Scales spacing across the entire app — cards, tables, and lists."
            >
              <SegmentedControl
                value={density}
                onChange={(v) => {
                  setDensity(v as 'comfortable' | 'compact')
                  showToast(`Density set to ${v}.`, 'info')
                }}
                options={[
                  { label: 'Comfortable', value: 'comfortable' },
                  { label: 'Compact', value: 'compact' },
                ]}
              />
            </SettingsRow>
            <SettingsRow
              title="Accent colour"
              description="Buttons, links and highlights, everywhere."
            >
              <div className="flex items-center gap-2">
                {Object.entries(ACCENT_OPTIONS).map(([hex, opt]) => (
                  <button
                    key={hex}
                    aria-label={`Set accent colour ${opt.label}`}
                    title={opt.label}
                    onClick={() => {
                      setAccentColor(hex)
                      showToast(`Accent colour set to ${opt.label}.`, 'info')
                    }}
                    className="relative h-7 w-7 rounded-full ring-2 ring-white transition-transform hover:scale-110"
                    style={{ backgroundColor: hex }}
                  >
                    {hex === accentColor && (
                      <span
                        className="absolute inset-0 flex items-center justify-center rounded-full"
                        style={{ boxShadow: `0 0 0 2px white, 0 0 0 4px ${hex}` }}
                      >
                        <Check size={13} className="text-white" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </SettingsRow>
          </div>
        )}

        {tab === 'account' && currentUser !== null && <AccountTab currentUser={currentUser} logout={logout} />}
      </div>
    </div>
  )
}
